import { PageContainer, PageHeader } from '@/components/Atoms'
import { useCrtVersionSelection } from '@/hooks/crt/useCrtVersionSelection'
import { useCurrentChannelMix } from '@/hooks/queries/crt/useChannelMix'
import { useConsumerRatesLastUpdated } from '@/hooks/queries/crt/useConsumerRates'
import { usePricingAssumptions } from '@/hooks/queries/crt/usePricingAssumptions'
import { useRetailerRatesLastUpdated } from '@/hooks/queries/crt/useRetailerRates'
import { useTaxAssumptions } from '@/hooks/queries/crt/useTaxAssumptions'
import { crtPeriodToParams } from '@/lib/utils/crtPeriodParams'
import { toUpdatedAt } from '@/lib/utils/getLastUpdatedState'
import { Insights, type InsightItem } from '@/routes/_auth/_modules/_shared/-components/Insights'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_auth/_modules/crt/pricing-excise/insights/')({
  component: PricingExciseInsight,
})

function PricingExciseInsight() {
  const { t } = useTranslation(['crt', 'common'])

  // A channel mix belongs to one CRT period, so the tile shows the header's period's mix.
  const { selection } = useCrtVersionSelection()
  const { data: channelMix } = useCurrentChannelMix(selection.crtPeriod)
  // Assumptions are picked per view, so a tile's freshness is the most recently created assumption.
  const { data: pricingAssumptions } = usePricingAssumptions({ pageSize: 1 })
  const { data: taxAssumptions } = useTaxAssumptions({ pageSize: 1 })
  const latestPricingAssumption = pricingAssumptions && (pricingAssumptions.results[0] ?? null)
  const latestTaxAssumption = taxAssumptions && (taxAssumptions.results[0] ?? null)
  const { data: retailerRatesLastUpdated } = useRetailerRatesLastUpdated()
  const { data: consumerRatesLastUpdated } = useConsumerRatesLastUpdated()

  const automatedInsights: InsightItem[] = [
    {
      name: t('route.crt.channelMix', { ns: 'common' }),
      frequency: t('insights.frequency.monthly'),
      frequencySchedule: { type: 'monthly', dayOfMonth: 5 },
      frequencyTooltip: t('pricingExciseInsight.channelMix.frequencyDetail'),
      updatedAt: toUpdatedAt(channelMix),
      description: t('pricingExciseInsight.channelMix.linkText'),
      link: channelMix
        ? {
            to: '/crt/pricing-excise/insights/channel-mix/$year/$month',
            params: crtPeriodToParams(channelMix.crtPeriod),
          }
        : { to: '/crt/pricing-excise/insights/channel-mix' },
      permissions: ['crt_channel_mix_view'],
    },
  ]

  const assumptions: InsightItem[] = [
    {
      name: t('route.crt.pricingExcise.pricingAssumptions', { ns: 'common' }),
      description: t('pricingExciseInsight.pricingAssumptions.linkText'),
      updatedAt: toUpdatedAt(latestPricingAssumption),
      isColorStateEnabled: false,
      link: { to: '/crt/pricing-excise/pricing-assumptions' },
      permissions: ['crt_pe_pricing_assumptions_view'],
    },
    {
      name: t('route.crt.pricingExcise.taxAssumptions', { ns: 'common' }),
      description: t('pricingExciseInsight.taxAssumptions.linkText'),
      updatedAt: toUpdatedAt(latestTaxAssumption),
      isColorStateEnabled: false,
      link: { to: '/crt/pricing-excise/tax-assumptions' },
      permissions: ['crt_pe_tax_assumptions_view'],
    },
    {
      name: t('route.crt.retailerRates', { ns: 'common' }),
      description: t('pricingExciseInsight.retailerRates.linkText'),
      updatedAt: toUpdatedAt(retailerRatesLastUpdated),
      link: { to: '/crt/pricing-excise/insights/retailer-rates' },
      permissions: ['crt_retailer_rates_view'],
    },
    {
      name: t('route.crt.consumerRates', { ns: 'common' }),
      description: t('pricingExciseInsight.consumerRates.linkText'),
      updatedAt: toUpdatedAt(consumerRatesLastUpdated),
      link: { to: '/crt/pricing-excise/insights/consumer-rates' },
      permissions: ['crt_consumer_rates_view'],
    },
  ]

  return (
    <PageContainer>
      <PageHeader>{t('pricingExciseInsight.pageHeader')}</PageHeader>
      <Insights automatedImports={automatedInsights} assumptions={assumptions} />
    </PageContainer>
  )
}
